import {
  Button,
  Card,
  Spinner,
} from "react-bootstrap";
import { FaMinus, FaPlus } from "react-icons/fa";
import {
  IoReloadOutline,
  IoSettingsSharp,
  IoSparklesOutline,
} from "react-icons/io5";
import Typewriter from "typewriter-effect";
import GradientText from "../../components/Logo";
import PieChartWithLegend from "../../components/shared/charts/PieChart";
import DonutScoreChart from "../../components/shared/charts/DonutScoreChart";
import AssetCard from "../../components/wallet/AssetCard";
import { useHistory } from "react-router-dom";
import useWallet from "../../hooks/useWallet";
import { useEffect, useState } from "react";
import { WalletInsightResponse } from "../../@types/Wallet";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import { BiAnalyse } from "react-icons/bi";
import LoadingBar from "react-top-loading-bar";
import LoadingQueue from "../../components/wallet/LoadingQueue";
import AddAssetModal from "../../components/wallet/AddAssetModal";
import RemoveAssetModal from "../../components/wallet/RemoveAssetModal";
import { AiOutlineInbox } from "react-icons/ai";
import ConfigureWallet from "../../components/wallet/ConfigureWallet";
import { isMobile } from "react-device-detect";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../context/GeneralContext";
import useAnalysisTypePreferences from "../../context/AnalysisTypePreferencesContext";
import { mockWalletData } from "../../components/wallet/data";

export default function Wallet() {
  const [walletData, setWalletData] = useState<WalletInsightResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState<boolean>(false);
  const [showRemoveAssetModal, setShowRemoveAssetModal] =
    useState<boolean>(false);
  const [showConfigureWalletModal, setShowConfigureWalletModal] = useState<boolean>(false);
  const history = useHistory();
  const { setIsOpen, setCurrentStep, setSteps, isOpen } = useTour();
  const { tourStart, setDisablePrev, setTourStart } = useGeneralContext();
  const { setPreferences } = useAnalysisTypePreferences();

  const { push } = useHistory();
  const { getInsights, dispatchToQueue } = useWallet();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      const response = await getInsights();

      if (response.status === 200) {
        setWalletData(response.data);
      } else if (response.status === 404 && !tourStart) {
        push('/setup-wallet');
      }

      setIsLoading(false);
    }

    fetchData();
  }, []);

  useEffect(() => {
    const walletSteps = [
      {
        selector: "#wallet-step-0",
        content: `Aqui no menu "Minha carteira" você pode conectar sua carteira de investimentos diretamente na nossa IA, dessa forma você pode adicionar seus ativos e configurações e ter um assistente pessoal para sua carteira, de maneira totalmente automatizada com nossos modelos de IA.`,
        position: "center",
        action: () => {
          setDisablePrev(true);
        },
        actionAfter: () => {
          setDisablePrev(false);
        },
      },
      {
        selector: "#wallet-step-1",
        content: "Aqui você pode adicionar seus ativos, como fundos imobiliários, ações e criptomoedas, e obter insights específicos de cada um no contexto da sua carteira.",
      },
      {
        selector: "#wallet-step-2 h2",
        content: "Obtenha um panorama detalhado da sua carteira, com recomendações personalizadas de alocação de recursos de acordo com o seu perfil de investidor, além de alertas sobre áreas que exigem maior cautela.",
      },
      {
        selector: "#wallet-step-3",
        content: "Nossa IA avalia sua carteira e gera um score baseado no seu perfil de investidor, que serve como uma métrica para refletir o sentimento em relação à sua carteira.",
      },
      {
        selector: "#wallet-step-4",
        content: "Confira a distribuição da sua carteira, o balanço atual, sua meta de balanço, e veja como tudo se alinha com a sua tolerância ao risco.",
        actionAfter: () => {
          if (setSteps) {
            setSteps([
              {
                selector: "#stepeable-Feed",
                content:
                  "No seu Feed, se você segue ativos, você consegue acompanhar postagens, reagir e interagir com outros usuários. Também tenho outras funções super interessantes, mas vou deixar você descobrir sozinho, te vejo em breve! 😊",
                position: "center",
                action: () => {
                  setTourStart(false);
                  if (localStorage.getItem("analysis_type_preferences")) {
                    setPreferences(
                      JSON.parse(
                        localStorage.getItem("analysis_type_preferences")!
                      )
                    );
                  }
                },
                actionAfter: () => {
                  setIsOpen(false);
                  setSteps([]);
                  document
                    .querySelector("#root > .container")
                    ?.classList.remove("disable-click");
                  document
                    .querySelector("#root .container")
                    ?.classList.remove("disable-click");
                  document.body?.classList.remove("show-popup-search-persist");
                },
              },
            ]);
            history.push("/dashboard");
          }
        },
      }
    ]

    if (setSteps && tourStart && !isLoading) {
      setTimeout(() => {
        // @ts-ignore
        setSteps(walletSteps);
        setIsOpen(true);
        setCurrentStep(0);
      }, 1500);
    }

  }, [isLoading])

  async function handleUpdateData() {
    setIsLoading(true);
    setWalletData(null);

    await dispatchToQueue();

    const interval: NodeJS.Timeout = setInterval(async () => {
      const walletDataResponse = await getInsights();

      if (walletDataResponse.status === 200) {
        setWalletData(walletDataResponse.data);
        setIsLoading(false);
        return clearInterval(interval);
      } else if (walletDataResponse.status === 404) {
        push('/setup-wallet');
      }
    }, 5000);
  }

  const iconSize = 22;

  if (isLoading && !tourStart) {
    return <LoadingState />;
  }

  const data = tourStart ? mockWalletData : walletData;

  if (data) {
    return (
      <div className="pd-t-20 pd-b-20">
        <AddAssetModal
          show={showAddAssetModal}
          callOnEnd={() => {
            setShowAddAssetModal(false);
            handleUpdateData();
          }}
          onClickClose={() => setShowAddAssetModal(false)}
        />

        <RemoveAssetModal
          show={showRemoveAssetModal}
          assetsInsights={data.insights.assets}
          onClickClose={() => setShowRemoveAssetModal(false)}
          callOnEnd={() => {
            setShowRemoveAssetModal(false);
            handleUpdateData();
          }}
        />
        <ConfigureWallet show={showConfigureWalletModal} onClickClose={() => setShowConfigureWalletModal(false)} callOnEnd={() => {
          setShowConfigureWalletModal(false);
          handleUpdateData();
        }} />

        <div className="row">
          <div className="col-lg-8">
            <div className="card card-dashboard-four materialized-card">
              <div className="card-header">
                <h4>
                  <GradientText text={"Lucy AI"} />
                  <IoSparklesOutline
                    style={{ color: "var(--primary-blue)" }}
                    size={25}
                  />
                </h4>
              </div>
              {/* card-header */}
              <div id="wallet-step-2" className="card-body" style={isOpen ? { overflow: "hidden", height: "70vh", maxHeight: "70vh" } : {}}>
                <Typewriter
                  onInit={(typewriter) => {
                    typewriter.typeString(
                      data.insights.wallet.ai_insight
                    );

                    typewriter.start();
                  }}
                  options={{
                    delay: tourStart ? 0 : 1,
                    cursorClassName: "d-none",
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-4 mg-t-20 mg-lg-t-0">
            <div className="row row-sm">
              {/* Scores Carteira */}
              <div className="col-md-6 col-lg-12 mg-b-20 mg-md-b-0 mg-lg-b-20">
                <div id="wallet-step-3" className="card card-dashboard-five materialized-card">
                  <div className="card-header">
                    <h6 className="tx-16 tx-primary tx-semibold">
                      Avaliação da carteira
                    </h6>
                    <span className="card-text">
                      Nosso modelo de IA avaliou sua carteira e determinou as
                      seguintes pontuações e sentimentos.
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-center">
                      <DonutScoreChart
                        score={data.insights.wallet.ai_score}
                      />
                    </div>

                    {SentimentListItem(data.insights.wallet.ai_sentiment)}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-12">
                <div id="wallet-step-4" className="card card-dashboard-pageviews materialized-card">
                  <div className="card-header">
                    <div className="d-flex">
                      <h6 className="tx-16 tx-primary tx-semibold">
                        Minha carteira
                      </h6>
                      <IoSettingsSharp
                        size={20}
                        className="ml-auto cursor-pointer"
                        onClick={() => setShowConfigureWalletModal(true)}
                      />
                    </div>
                    <p className="card-text">
                      Confira alguns detalhes importantes da sua carteira e
                      visualize um gráfico de diversificação de ativos.
                    </p>
                  </div>
                  {/* card-header */}
                  <div className="card-body">
                    {data.diversification.total > 0 ? (
                      <PieChartWithLegend
                        data={[
                          {
                            name: "Criptos",
                            value: data.diversification.criptos,
                            color: "#cc0000",
                          },
                          {
                            name: "Ações",
                            value: data.diversification.stocks,
                            color: "#3333ff",
                          },
                          {
                            name: "FIIs",
                            value: data.diversification.funds,
                            color: "#009900",
                          },
                        ]}
                        height={280}
                        label="Ativos"
                        labelTotal={data.diversification.total}
                      />
                    ) : (
                      <></>
                    )}

                    <div className="az-list-item">
                      <div>
                        <h6>Balanco atual</h6>
                        <span>Saldo total em sua carteira.</span>
                      </div>
                      <div>
                        <h6 className="tx-primary">
                          {data.wallet_details.balance}
                        </h6>
                      </div>
                    </div>
                    <div className="az-list-item">
                      <div>
                        <h6>Balanço meta</h6>
                        <span>Meta de saldo futuro.</span>
                      </div>
                      <div>
                        <h6 className="tx-primary">
                          {data.wallet_details.balance_goal}
                        </h6>
                      </div>
                    </div>
                    {getToleranceListItem(data.wallet_details.tolerance)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="separator mg-t-20 mg-b-20">
          Última atualização dos dados em{" "}
          {moment(data.updated_at).format("DD/MM/YYYY [ás] HH:mm")}
        </div>

        {!isMobile ? <Card className="mg-b-20  materialized-card">
          <Card.Body
            className="d-flex justify-content-center flex-wrap"
            style={{ gap: 20 }}
          >
            <Button
              className="rounded-button"
              onClick={() => handleUpdateData()}
            >
              Atualizar dados <IoReloadOutline size={iconSize} />
            </Button>
            <Button
              className="rounded-button"
              onClick={() => setShowAddAssetModal(true)}
            >
              Adicionar ativos <FaPlus size={iconSize} />
            </Button>
            <Button
              className="rounded-button"
              onClick={() => setShowRemoveAssetModal(true)}
            >
              Remover ativos <FaMinus size={iconSize} />
            </Button>
            <Button className="rounded-button" onClick={() => setShowConfigureWalletModal(true)}>
              Configurar carteira <IoSettingsSharp size={iconSize} />
            </Button>
          </Card.Body>
        </Card> : <></>}
        {isMobile ? <Card className="mg-b-20  materialized-card">
          <Card.Body
            className="d-flex justify-content-center flex-wrap"
            style={{ gap: 20 }}
          >
            <Button
              className="rounded-button"
              onClick={() => handleUpdateData()}
            >
              Atualizar dados <IoReloadOutline size={iconSize} />
            </Button>
            <Button className="rounded-button" onClick={() => setShowConfigureWalletModal(true)}>
              Configurar carteira <IoSettingsSharp size={iconSize} />
            </Button>
          </Card.Body>
        </Card> : <></>}

        <div className="content-wrapper w-100">
          <div className="row">
            <div className="col-md-12">
              <div className="card materialized-card">
                <div className="card-body">
                  <div className="d-flex">
                    <div className="wrapper">
                      <h4 className="tx-18 tx-primary tx-semibold">
                        Meus ativos
                      </h4>
                      <p>Total de ativos: {data.diversification.total}</p>
                    </div>
                    <div className="wrapper"></div>
                  </div>
                  {isMobile ?
                    <div
                      className="d-flex flex-wrap justify-content-center mg-b-20"
                      style={{ gap: 20 }}
                    >
                      <Button
                        className="rounded-button"
                        onClick={() => setShowAddAssetModal(true)}
                      >
                        Adicionar ativos <FaPlus size={iconSize} />
                      </Button>
                      <Button
                        className="rounded-button"
                        onClick={() => setShowRemoveAssetModal(true)}
                      >
                        Remover ativos <FaMinus size={iconSize} />
                      </Button>
                    </div>
                    : <></>}
                  {data.diversification.total === 0 ? (
                    <div className="d-flex justify-content-center">
                      <div className="text-center pd-b-40">
                        <span>
                          <AiOutlineInbox size={48} />
                        </span>
                        <h2>Sem ativos na carteira.</h2>
                        <span>
                          Adicione ativos em sua carteira para ter uma avaliação
                          completa.
                          <br />
                          Por favor, <strong>Adicione ativos.</strong>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="row project-list-showcase">
                    {data.insights.assets.map((asset, index) => {
                      return (
                        <div id={index === 0 ? "wallet-step-1" : ""} className="col-12 col-md-6 col-lg-4 project-grid">
                          <AssetCard
                            asset_details={asset.asset_details}
                            ai_insight={asset.ai_insight}
                            ai_risk_level={asset.ai_risk_level}
                            ai_score={asset.ai_score}
                            ai_sentiment={asset.ai_sentiment}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="pd-t-20 pd-b-20">
        <Card className="materialized-card">
          <Card.Body>
            <div className="text-center">
              <BiAnalyse color="var(--primary-blue)" size={50} />
              <h1>Atualizar dados</h1>
              <p className="tx-16">
                Nosso modelo de I.A precisa se atualizar com os dados da sua
                carteira, toque no botão abaixo para iniciar.
              </p>
              <p className="tx-12 tx-warning">
                A primeira atualização pode demorar alguns segundos, por favor,
                tenha paciência.
              </p>
              <Button
                onClick={() => handleUpdateData()}
                className="rounded-button"
              >
                Atualizar dados
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

function LoadingState() {
  const [progressBar, setProgressBar] = useState<number>(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (progressBar < 95) {
        setProgressBar((val) => val + 5);
      }
    }, 4500);

    return () => clearInterval(intervalId);
  }, [progressBar]);

  return (
    <div className="pd-t-20 pd-b-20">
      <LoadingBar
        color={"#596EFD"}
        height={3}
        shadow={true}
        loaderSpeed={3500}
        progress={progressBar}
      />
      <div className="row">
        <div className="col-lg-8">{<LoadingQueue show />}</div>
        <div className="col-lg-4 mg-t-20 mg-lg-t-0">
          <div className="row row-sm">
            {/* Scores Carteira */}
            <div className="col-md-6 col-lg-12 mg-b-20 mg-md-b-0 mg-lg-b-20">
              <div className="card card-dashboard-five materialized-card h-100">
                <div className="card-header">
                  <h6 className="tx-16 tx-primary tx-semibold">
                    Avaliação da carteira
                  </h6>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-12">
              <div className="card card-dashboard-pageviews materialized-card h-100">
                <div className="card-header">
                  <div className="d-flex">
                    <h6 className="tx-16 tx-primary tx-semibold">
                      Minha carteira
                    </h6>
                  </div>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content-wrapper w-100 mg-t-20">
        <div className="row">
          <div className="col-md-12">
            <div className="card materialized-card">
              <div className="card-body">
                <div className="d-flex">
                  <div className="wrapper">
                    <h4 className="tx-18 tx-primary tx-semibold">
                      Meus ativos
                    </h4>
                  </div>
                  <div className="wrapper"></div>
                </div>
                <div className="row project-list-showcase">
                  {[0, 1, 2].map((item) => {
                    return (
                      <div className="col-12 col-md-6 col-lg-4 project-grid">
                        <div
                          className="project-grid-inner materialized-card h-100"
                          key={item}
                        >
                          <div className="d-flex align-items-start pd-b-20">
                            <div className="d-flex">
                              <Skeleton
                                width={30}
                                height={30}
                                borderRadius={50}
                              />
                            </div>
                          </div>
                          <Skeleton count={3} />

                          <br />
                          <Skeleton count={2} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SentimentListItem(sentiment: number) {
  let sentimentColor = "";
  let sentimentText = "";

  switch (sentiment) {
    case 0:
      sentimentColor = "tx-danger";
      sentimentText = "CUIDADO";
      break;
    case 1:
      sentimentColor = "tx-warning";
      sentimentText = "ATENÇÃO";
      break;
    case 2:
      sentimentColor = "tx-secondary";
      sentimentText = "NEUTRO";
      break;
    case 3:
      sentimentColor = "tx-success";
      sentimentText = "EXCELENTE";
      break;
    default:
      sentimentColor = "tx-secondary";
      sentimentText = "NEUTRO";
  }

  return (
    <div className="az-list-item">
      <div>
        <h6>Sentimento da IA</h6>
        <span>Ao avaliar sua carteira.</span>
      </div>
      <div>
        <h6 className={sentimentColor}>{sentimentText}</h6>
      </div>
    </div>
  );
}

function getToleranceListItem(tolerance: string) {
  let color = "";
  let text = "";

  switch (tolerance) {
    case "low":
      color = "danger";
      text = "BAIXA";
      break;
    case "high":
      color = "success";
      text = "ALTA";
      break;
    default:
      color = "secondary";
      text = "?";
  }

  return (
    <div className="az-list-item">
      <div>
        <h6>Tolerância</h6>
        <span>Se refere ao quanto você suporta perder.</span>
      </div>
      <div>
        <h6 className={`tx-${color}`}>{text}</h6>
      </div>
    </div>
  );
}
